import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import SEO from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";
import { BreadcrumbSchema } from "../components/SchemaMarkup";
import {
  ArrowRight,
  Calendar,
  Clock,
  User,
  Search,
  Tag,
} from "lucide-react";
import { Input } from "@/components/ui/input";

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`);
        setPosts(response.data);
        setFilteredPosts(response.data);
      } catch (err) {
        console.error("Error fetching blog posts:", err);
      }
      setLoading(false);
    };
    fetchPosts();
  }, []);

  useEffect(() => {
    let filtered = posts;

    if (searchQuery) {
      filtered = filtered.filter(
        (post) =>
          post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedCategory !== "All") {
      filtered = filtered.filter((post) => post.category === selectedCategory);
    }

    setFilteredPosts(filtered);
  }, [searchQuery, selectedCategory, posts]);

  const categories = [
    "All",
    ...new Set(posts.map((post) => post.category)),
  ];

  const featuredPost = posts[0];

  return (
    <div className="pt-32" data-testid="blog-page">
      <SEO 
        title="SEO & Marketing Blog"
        description="Expert insights on SEO strategy, marketing automation, AI marketing, lead generation, CRM optimization, and business growth from OptimaGrowth."
        keywords="SEO blog, marketing automation tips, AI marketing insights, lead generation strategies, CRM best practices, digital marketing blog South Africa"
      />
      <BreadcrumbSchema items={[{ name: "Home", url: "/" }, { name: "Blog" }]} />
      <Breadcrumb items={[{ name: "Blog" }]} />
      {/* Hero Section */}
      <section className="section pb-0" data-testid="blog-hero">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="badge badge-blue mb-4">Blog</span>
              <h1 className="font-heading text-4xl lg:text-5xl font-bold text-optima-dark mb-6">
                SEO & Marketing Automation Insights
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                Expert insights on marketing automation, business growth, and operational efficiency.
              </p>

              {/* Search */}
              <div className="relative max-w-md mx-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 input-field"
                  data-testid="blog-search-input"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && (
        <section className="section pb-0" data-testid="featured-post">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Link
                to={`/blog/${featuredPost.slug}`}
                className="block group"
                data-testid="featured-post-link"
              >
                <div className="grid lg:grid-cols-2 gap-8 items-center bg-slate-50 rounded-2xl overflow-hidden">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={featuredPost.featured_image}
                      alt={`${featuredPost.title} - OptimaGrowth SEO and marketing automation blog`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-8">
                    <span className="badge badge-pink mb-4">Featured</span>
                    <h2 className="font-heading text-2xl lg:text-3xl font-bold text-optima-dark mb-4 group-hover:text-optima-blue transition-colors">
                      {featuredPost.title}
                    </h2>
                    <p className="text-slate-600 mb-6">{featuredPost.excerpt}</p>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {featuredPost.published_date}
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {featuredPost.read_time}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* Categories */}
      <section className="py-8" data-testid="blog-categories">
        <div className="container-custom">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? "bg-optima-blue text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid={`category-btn-${category.toLowerCase().replace(' ', '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="section" data-testid="blog-grid">
        <div className="container-custom">
          {loading && (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-optima-blue border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          )}
          {!loading && filteredPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-600">No articles found matching your search.</p>
            </div>
          )}
          {!loading && filteredPosts.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.slice(1).map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link
                    to={`/blog/${post.slug}`}
                    className="card block group overflow-hidden h-full"
                    data-testid={`blog-card-${post.slug}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={post.featured_image}
                        alt={`${post.title} - marketing automation and SEO insights`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <span className="inline-block px-3 py-1 bg-optima-blue/10 text-optima-blue text-xs font-medium rounded-full mb-3">
                        {post.category}
                      </span>
                      <h3 className="font-heading text-lg font-semibold text-optima-dark mb-2 group-hover:text-optima-blue transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3 h-3" />
                          {post.published_date}
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          {post.read_time}
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-optima-dark" data-testid="blog-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-3xl font-bold text-white mb-6">
              Ready to Apply These Strategies?
            </h2>
            <p className="text-white/80 text-lg mb-6">
              Book a free growth audit and let's discuss how to implement these insights in your business.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8 text-white/70">
              <a href="mailto:info@optimagrowth.agency" className="hover:text-white transition-colors">
                info@optimagrowth.agency
              </a>
              <span className="hidden sm:block">•</span>
              <a href="tel:+27661229790" className="hover:text-white transition-colors">
                +27 66 122 9790
              </a>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-optima-blue rounded-full font-semibold hover:bg-slate-50 transition-colors"
              data-testid="blog-cta-btn"
            >
              Book a Free Growth Audit <ArrowRight className="w-5 h-5" />
            </Link>
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <Link to="/services" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Explore Services</Link>
              <Link to="/pricing" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">View Pricing</Link>
              <Link to="/case-studies" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Case Studies</Link>
              <Link to="/client-onboarding" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Start Onboarding</Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
